// Actions
import AreaActions from 'app/shared/flux/actions/AreaActions';
import ErrorActions from 'app/shared/flux/actions/ErrorActions';

// Misc / Utils
import fetchAreaWrapperController from 'app/shared/wrappers/FetchAreaWrapper/controller';
import { getGlobalLogger } from '@zg-rentals/logger-base';

// Types
import type { ThunkDispatch } from 'redux-thunk';
import type { AnyAction } from 'redux';
import type { RootReduxState } from 'app/shared/flux/reducers/index';

// Define interfaces for better type safety
interface Trace {
  finishAsyncSpan: (params: { marker: string }) => void;
}

interface ReduxStore {
  getState: () => RootReduxState;
  dispatch: ThunkDispatch<RootReduxState, undefined, AnyAction>;
}

interface RouteState {
  pathname: string;
  query?: Record<string, string | number | boolean | undefined>;
}

interface ReactContext {
  store: ReduxStore;
  trace: Trace;
  state: RouteState;
}

const logger = getGlobalLogger('wrappers/fetcharea/loadareaseolinks');

const loadAreaSeoLinks = (
  reactContext: ReactContext,
  { areaResourceId }: { areaResourceId: string },
): Promise<unknown> => {
  const { store, trace } = reactContext;
  const { pathname } = reactContext.state;
  const seoInfo = fetchAreaWrapperController.getAreaSeoInfo(pathname);
  const searchSlug = seoInfo.searchSlug || 'apartments-for-rent';

  // Guard: Only fetch SEO links if areaResourceId is defined to prevent /undefined/ URLs
  if (!areaResourceId) {
    logger?.error('Missing areaResourceId when loading SEO links in loadAreaSeoLinks', {
      pathname,
      resourceId: areaResourceId,
      searchSlug,
    });

    trace.finishAsyncSpan({
      marker: 'FetchAreaWrapper#AreaActions#loadAreaSeoLinks',
    });

    return Promise.resolve(null);
  }

  return store
    .dispatch(AreaActions.loadAreaSeoLinks({ resourceId: areaResourceId, searchSlug }))
    .then((response: unknown) => {
      trace.finishAsyncSpan({
        marker: 'FetchAreaWrapper#AreaActions#loadAreaSeoLinks',
      });

      return response;
    })
    .catch((error: Error) => {
      logger?.error('Error loading SEO links', {
        error,
        errorLocation: 'component.areaPage.serverRouteWillMount#loadAreaSeoLinks',
        resourceId: areaResourceId,
        pathname,
        searchSlug,
      });

      store.dispatch(
        ErrorActions.errorHandler({
          error,
          errorLocation: 'component.areaPage.serverRouteWillMount#loadAreaSeoLinks',
          errorClass: 'areaActions',
        }),
      );
    });
};

export default loadAreaSeoLinks;
